import { addImageParams, AddImageParamTypes, getCatalogUrl } from '@/utils/urls';
import { AmpProduct, useTrackAmpClicked, useTrackAmpSeen } from '@/utils/amplitude/analytics';
import { ArrowRight } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary, Caption, H2, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { CatalogAds } from '@/redux/modules/catalog/ad/catalogAd.types';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { getCatalogAd } from '@/redux/modules/catalog/ad/catalogAd.selectors';
import {
    getCatalogSellerId,
    getCatalogSellerName,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { MouseEvent, useEffect, useMemo, useRef } from 'react';
import { trackAdUnitClick, trackAdUnitVisible } from '@/redux/modules/analytics';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useNavigate } from 'react-router-dom';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import useSSRFriendlyDate from '@/utils/useSSRFriendlyDate';
import useVisibilitySensor from '@rooks/use-visibility-sensor';

const imageParams: AddImageParamTypes = {
    height: 175,
    width: 175,
};

export const SearchResultsAdExperiment = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { formatMessage } = useIntl();
    const isMobile = useIsMobileLayout();
    const adRef = useRef<HTMLAnchorElement>(null);
    const { isVisible } = useVisibilitySensor(adRef, {
        intervalCheck: false,
        partialVisibility: true,
        resizeCheck: true,
        scrollCheck: true,
    });
    const previousAdVisible = usePrevious(isVisible);

    const trackAmpClicked = useTrackAmpClicked();
    const trackAmpSeen = useTrackAmpSeen();

    const { adUnitId, catalogId, consignment, imageUrl1, imageUrl2, imageUrl3, saleStartTs } = useAppSelector((state) =>
        getCatalogAd(state, { pageName: CatalogAds.SearchPageBanner })
    );

    const catalogTitle = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));

    const clientSaleStartTs = useSSRFriendlyDate(saleStartTs);

    const catalogUrl = getCatalogUrl(catalogId, catalogTitle);

    const trackAndNavigate = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        dispatch(
            trackAdUnitClick({
                adUnitId,
                consignment: Boolean(consignment),
                page: 'Search Page Banner',
            })
        );
        trackAmpClicked({ ampProduct: AmpProduct.SearchPageBanner, catalogId, houseId: sellerId });
        navigate(catalogUrl);
    };
    const imageUrls = useMemo(() => {
        return [imageUrl1, imageUrl2, imageUrl3].filter((image) => Boolean(image));
    }, [imageUrl1, imageUrl2, imageUrl3]);

    useEffect(() => {
        if (Boolean(catalogId)) {
            dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] }));
        }
    }, [catalogId, dispatch]);

    useEffect(() => {
        if (!previousAdVisible && isVisible && Boolean(adUnitId)) {
            dispatch(
                trackAdUnitVisible({
                    adUnitId,
                    page: 'Search Page Banner',
                })
            );
            trackAmpSeen({ ampProduct: AmpProduct.SearchPageBanner, catalogId, houseId: sellerId });
        }
    }, [adUnitId, catalogId, consignment, dispatch, isVisible, previousAdVisible, sellerId, trackAmpSeen]);

    if (!Boolean(catalogId)) {
        return null;
    }

    return (
        <section
            className="ax-w-[1200px] mx-auto mt-[40px] hover:cursor-pointer"
            data-testid="searchResultsAdUnit"
            onClick={(e) => {
                trackAndNavigate(e);
            }}
            ref={adRef}
        >
            <div className={cn('relative rounded-md bg-background-inverse', isMobile ? 'p-sm' : 'p-md')}>
                <div className={cn('flex', isMobile ? 'mb-xs flex-col gap-sm' : 'flex-row gap-md')}>
                    <div className="w-full max-w-[725px] rounded-md bg-primary-700 p-md text-center">
                        <H2
                            className="mb-xs"
                            color="inverse"
                        >
                            {catalogTitle}
                        </H2>
                        <H6
                            className="mb-2xs"
                            color="inverse"
                        >
                            {clientSaleStartTs}
                        </H6>
                        <BodySecondary color="inverse">{sellerName}</BodySecondary>
                    </div>

                    <div className={cn('flex flex-row justify-around rounded-md', isMobile ? 'gap-xs' : 'gap-md')}>
                        {imageUrls.map((imageUrl) => (
                            <Link
                                className={cn(
                                    'flex items-center justify-center rounded-md bg-background-primary',
                                    isMobile ? 'h-[118px] w-[118px]' : 'h-[176px] w-[176px]'
                                )}
                                href={catalogUrl}
                                key={`${imageUrl}-catalog-banner-ad-image`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    trackAndNavigate(e);
                                }}
                            >
                                <img
                                    className={cn(
                                        'rounded-md object-contain',
                                        isMobile ? 'max-h-[104px] max-w-[104px]' : 'max-h-[176px] max-w-[176px]'
                                    )}
                                    height={String(imageParams.height)}
                                    src={addImageParams({ ...imageParams, url: imageUrl })}
                                    width={String(imageParams.width)}
                                />
                            </Link>
                        ))}
                    </div>

                    <Link
                        className={cn(
                            'flex w-full flex-row rounded-md border border-border-inverse p-sm no-underline hover:bg-primary-700 hover:no-underline',
                            isMobile ? 'justify-between' : 'max-w-[176px] items-end gap-xs'
                        )}
                        onClick={(e) => {
                            e.stopPropagation();
                            trackAndNavigate(e);
                        }}
                    >
                        <H2
                            className="uppercase"
                            color="inverse"
                        >
                            {formatMessage({ id: 'searchPage.searchResultsAdExperiment.exploreAuction' })}
                        </H2>
                        <ArrowRight
                            className={cn({ 'mb-2xs': !isMobile })}
                            color="inverse"
                            size="md"
                        />
                    </Link>
                </div>
                <Caption
                    className={cn('absolute bottom-2xs', isMobile ? 'right-sm' : 'left-md')}
                    color="inactive"
                >
                    {formatMessage({ id: 'searchPage.searchResultsAdExperiment.sponsored' })}
                </Caption>
            </div>
        </section>
    );
};
